<template lang="pug">
  .distributor-map-wrap
    .distributor-map(:style="heightVars" ref="mapContainer")
    Drawer(
      v-if="selectedPin"
      title="Distributor Details"
      position="right"
      @hide="closeDrawer"
    )
      .distributor-pin-details
        .detail-group
          .detail-label {{ __('Company Name') }}
          .company-name {{ selectedPin.name }}
        .detail-group
          .detail-label {{ __('Address') }}
          .address
            div {{ selectedPin.addressLine1 }}
            div(v-if="selectedPin.addressLine2") {{ selectedPin.addressLine2 }}
            div {{ [selectedPin.city, [selectedPin.state, selectedPin.postcode].filter(Boolean).join(' '), selectedPin.country].filter(Boolean).join(', ') }}
        .detail-group
          .detail-label {{ __('Phone') }}
          .contact-link(:href="`tel:${selectedPin.phone}`") {{ selectedPin.phone }}
        .detail-group
          .detail-label {{ __('Email') }}
          a.contact-link(:href="`mailto:${selectedPin.email}`") {{ selectedPin.email }}
        .detail-group(v-if="selectedPin.websiteUrl")
          .detail-label {{ __('Website') }}
          a.contact-link(:href="selectedPin.websiteUrl" target="_blank" rel="noopener noreferrer") {{ formatDisplayUrl(selectedPin.websiteUrl) }}
</template>
<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import Drawer from '../core/Drawer.vue'
import { useRogue } from '../../js/rogue'
import { useMQ } from '../../js/mq'
import type { DistributorPin } from './distributorMapTypes'
import { formatDisplayUrl } from './distributorMapHelpers'

const MAPBOX_VERSION = '3.25.0'

const rogue = useRogue()
const mq = useMQ()
const mapContainer = ref<HTMLElement>()

const props = withDefaults(defineProps<{
  pins?: DistributorPin[]
  centerLat?: number
  centerLng?: number
  zoom?: number
  mobileCenterLat?: number
  mobileCenterLng?: number
  mobileZoom?: number
  height?: string
  mobileHeight?: string
  mapStyle?: string
}>(), {
  pins      : () => [],
  centerLat : 20,
  centerLng : 0,
  zoom      : 1.5,
  height    : '600px',
  mapStyle  : 'mapbox://styles/mapbox/dark-v11',
})

const effectiveCenter = computed<[number, number]>(() => {
  if (mq.value === 'mobile' && (props.mobileCenterLat !== undefined || props.mobileCenterLng !== undefined)) {
    return [props.mobileCenterLng ?? props.centerLng, props.mobileCenterLat ?? props.centerLat]
  }

  return [props.centerLng, props.centerLat]
})

const effectiveZoom = computed(() => (mq.value === 'mobile' && props.mobileZoom !== undefined ? props.mobileZoom : props.zoom))

const heightVars = computed(() => ({
  '--distributor-map-height'        : props.height,
  '--distributor-map-mobile-height' : props.mobileHeight,
}))

const loadScript = inject<(args: { script: string, async?: boolean }) => Promise<void>>('loadScript', async () => {})

const mapboxAccessToken = (rogue as unknown as { keys?: { mapbox?: { mapboxGlJs?: string } } }).keys?.mapbox?.mapboxGlJs

let map: any
const selectedPin = ref<DistributorPin | null>(null)
let lastFocusedElement: HTMLElement | null = null
const markers: { pin: DistributorPin, element: HTMLElement, marker: any }[] = []

function hasValidCoordinates(pin: DistributorPin) {
  return typeof pin.latitude === 'number' && typeof pin.longitude === 'number'
    && !Number.isNaN(pin.latitude) && !Number.isNaN(pin.longitude)
    && Math.abs(pin.latitude) <= 90 && Math.abs(pin.longitude) <= 180
}

const validPins = computed(() => props.pins.filter(pin => {
  const valid = hasValidCoordinates(pin)
  if (!valid) {
    rogue.log?.warn(`DistributorMap: skipping pin "${pin.name}" - missing or invalid coordinates`)
  }

  return valid
}))

function selectPin(pin: DistributorPin, el: HTMLElement) {
  lastFocusedElement = el
  selectedPin.value = pin
  markers.forEach(m => m.element.classList.toggle('active', m.pin === pin))
}

function closeDrawer() {
  selectedPin.value = null
  markers.forEach(m => m.element.classList.remove('active'))
  lastFocusedElement?.focus()
  lastFocusedElement = null
}

function createMarkerElement(pin: DistributorPin): HTMLElement {
  const el = document.createElement('div')
  el.className = 'distributor-pin'
  el.classList.toggle('featured', !!pin.featured)
  el.setAttribute('role', 'button')
  el.setAttribute('tabindex', '0')
  el.setAttribute('aria-label', pin.name)

  el.addEventListener('click', () => selectPin(pin, el))
  el.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      selectPin(pin, el)
    }
  })

  return el
}

function loadMapboxCss() {
  const href = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.css`
  if (document.querySelector(`link[href="${href}"]`)) return

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

onMounted(async () => {
  loadMapboxCss()

  await loadScript({ script: `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.js`, async: true })

  // @ts-ignore
  if (!window.mapboxgl) return

  // @ts-ignore
  window.mapboxgl.accessToken = mapboxAccessToken

  // @ts-ignore
  map = new window.mapboxgl.Map({
    container          : mapContainer.value!,
    style              : props.mapStyle,
    projection         : 'mercator',
    center             : effectiveCenter.value,
    zoom               : effectiveZoom.value,
    attributionControl : false,
  })

  // @ts-ignore
  map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }))

  map.on('load', () => {
    validPins.value.forEach(pin => {
      const element = createMarkerElement(pin)
      // @ts-ignore
      const marker = new window.mapboxgl.Marker({ element, anchor: 'bottom' })
        .setLngLat([pin.longitude, pin.latitude])
        .addTo(map)

      markers.push({ pin, element, marker })
    })
  })
})

onUnmounted(() => {
  map?.remove()
  map = undefined
})
</script>
<style lang="scss" scoped>
.distributor-map-wrap {
  width: 100%;
  margin-bottom: $space-xxxl;
}

.distributor-map {
  width: 100%;
  height: var(--distributor-map-height, 600px);
  min-height: 300px;
  position: relative;
  background-color: $color-black-light;

  @include mobile() {
    height: var(--distributor-map-mobile-height, var(--distributor-map-height, 600px));
  }
}

.distributor-pin-details {
  display: flex;
  flex-direction: column;
  gap: $space-xs;
  padding: $space-s;
  padding-bottom: 0;

  .detail-label {
    font-size: $size-xxs;
    font-weight: bold;
    text-transform: uppercase;
    color: $color-gray-dark;
    margin-bottom: $space-xxxs;
  }

  .company-name {
    font-size: $size-m;
    font-weight: bold;
  }

  .contact-link {
    display: block;
    margin-bottom: $space-xxs;
  }
}
</style>
<style lang="scss">
.distributor-pin {
  width: 19px;
  height: 25px;
  background-image: url('https://assets.roguefitness.com/Rogue%20Equipped%20Gyms%20Map/Pin%20Icons/png/white-red-pin.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $color-white;
    outline-offset: 2px;
  }

  &.active {
    transform: scale(1.2);
  }

  &.featured {
    background-image: url('https://assets.roguefitness.com/image/upload/v1788528123/Rogue%20Equipped%20Gyms%20Map/Pin%20Icons/png/R-pin-small_apabay.png');
    height:25px;
    width:19px;

    &.active {
      transform: scale(1.15);
    }
  }
}

.distributor-map {
  .mapboxgl-ctrl-logo {
    display: none !important;
  }
}
</style>
